import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { api } from "../../lib/apiClient";
import {
  describeAuthError,
  errorsOnBlur,
  errorsOnSubmit,
  errorsOnType,
  validateEmail,
  type ShownErrors,
} from "../../lib/authForm";
import { authEmailDraft, rememberAuthEmail } from "../../lib/authEmail";
import { useResend } from "../../lib/useResend";
import { Spinner } from "../../components/ui";
import {
  AuthBody,
  AuthField,
  AuthFooter,
  AuthHeading,
  AuthPending,
  FieldError,
  SentToTile,
} from "../../components/auth-card";
import { ArrowRightIcon } from "../../components/icons";

export const Route = createFileRoute("/auth/forgot")({
  // `next`: carried back to sign-in, so the round trip still ends where the
  // sign-in was headed (the /link approval, the setup wizard's claim).
  validateSearch: z.object({ next: z.string().optional().catch(undefined) }),
  component: Forgot,
  pendingComponent: AuthPending,
});

/** 202 for any address — the API never says whether it has an account. */
function sendResetLink(email: string) {
  return api("/v1/auth/password/forgot", { body: { email }, auth: false });
}

function Forgot() {
  const { next } = Route.useSearch();
  const [email, setEmail] = useState(authEmailDraft);
  // Where the link went. Set = the "Check your email" view, on the same URL.
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [errors, setErrors] = useState<ShownErrors<"email">>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const problem = validateEmail(email);
    setErrors(errorsOnSubmit(problem ? { email: problem } : {}));
    setFailure(null);
    if (problem) {
      emailRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await sendResetLink(email.trim());
      setSentTo(email.trim());
    } catch (err) {
      setFailure(describeAuthError(err, "forgot").message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {sentTo ? (
        <SentView email={sentTo} onUseAnother={() => setSentTo(null)} />
      ) : (
        <AuthBody>
          <AuthHeading title="Reset your password">
            We'll email you a reset link if the address has an account.
          </AuthHeading>
          <form onSubmit={submit} noValidate className="flex flex-col gap-6">
            <AuthField
              id="email"
              label="Email"
              error={errors.email?.message ?? failure ?? undefined}
              alert={errors.email ? errors.email.alert : true}
            >
              <input
                ref={emailRef}
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                className="rago-input rago-input-lg"
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  rememberAuthEmail(value);
                  setErrors((prev) => errorsOnType(prev, "email", validateEmail(value)));
                  setFailure(null);
                }}
                onBlur={() => setErrors((prev) => errorsOnBlur(prev, "email", email, validateEmail(email)))}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email || failure ? "email-error" : undefined}
              />
            </AuthField>
            <button type="submit" className="rago-btn rago-btn-block rago-btn-lg" disabled={busy}>
              {busy ? <Spinner /> : null}
              Send reset link
            </button>
          </form>
        </AuthBody>
      )}
      <AuthFooter prompt="Remembered it?">
        <Link to="/auth/signin" search={{ next }} className="rago-auth-switch">
          Back to sign in
          <ArrowRightIcon size={13} bold />
        </Link>
      </AuthFooter>
    </>
  );
}

/**
 * "Check your email": where the link went, what to try when it doesn't
 * show, and two ways on. Resend rests for a minute after each resend (the
 * API allows 5 per 15 min) — aria-disabled, not disabled, so the keyboard
 * focus stays on it — and a status line says it went.
 */
function SentView(props: { email: string; onUseAnother: () => void }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { state: resend, error, resend: sendAgain } = useResend(
    () => sendResetLink(props.email),
    (err) => describeAuthError(err, "forgot").message,
  );

  // The form this view replaced held the focus; the heading takes it.
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <AuthBody>
      <AuthHeading ref={headingRef} title="Check your email">
        If there's an account for this address, a reset link is on its way.
      </AuthHeading>
      <div className="flex flex-col gap-4">
        <SentToTile email={props.email} />
        <p className="text-center text-[14px] leading-normal text-balance text-[var(--rago-text-3)]">
          Can't find it? Check your spam folder, or make sure this is the address you signed up with.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {/* No min-w-0: the pair wraps (phones) only when a label can't fit. */}
        <div className="rago-btn-pair flex flex-wrap gap-2.5 text-[15px]">
          <button
            type="button"
            className="rago-btn rago-btn-quiet h-[50px] flex-1 whitespace-nowrap"
            aria-disabled={resend !== "idle" ? true : undefined}
            onClick={() => void sendAgain()}
          >
            {resend === "busy" ? <Spinner /> : null}
            {resend === "sent" ? "Sent again" : "Resend link"}
          </button>
          <button
            type="button"
            className="rago-btn rago-btn-quiet h-[50px] flex-1 whitespace-nowrap"
            onClick={props.onUseAnother}
          >
            Use another email
          </button>
        </div>
        <p role="status" className="sr-only">
          {resend === "sent" ? "Reset link sent again." : ""}
        </p>
        {error ? (
          <FieldError id="resend-error" alert>
            {error}
          </FieldError>
        ) : null}
      </div>
    </AuthBody>
  );
}
